import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { createQueueClient, type AlertView, type QueueClient } from '../alerts/queue-client.js';
import './browser-source.css';
import { AlertRender } from './alerts/AlertRender.js';
import { ALERT_POSITIONS, pickEnum } from './alerts/style-values.js';
import './overlay.css';

/**
 * The page an OBS Browser Source points at (Alerts brief §13, §14).
 *
 * No session, no chrome, no navigation — see the routing comment in
 * `App.tsx` for why. It reads the output token from `?k=`, polls for what
 * should be showing, displays exactly one alert at a time, and acks its
 * start and completion.
 *
 * Untrusted content (§21, §57): alert text is chat-derived and is rendered
 * ONLY as a React text child below, never through `dangerouslySetInnerHTML`.
 * A viewer whose display name is markup must appear on stream as characters,
 * not run as HTML.
 */

/** How long to wait before re-polling an empty queue, absent a nudge. */
const IDLE_POLL_MS = 4_000;

/** Same fact the API states in `OutputTokenError`; told the same way here. */
const INVALID_TOKEN_MESSAGE = 'This overlay link is not valid.';
const MISSING_TOKEN_MESSAGE = 'This overlay link is missing its token.';

/**
 * Matches the class `overlay.css` scopes its `body` rules to.
 *
 * Applied to `document.body` for exactly as long as this page is mounted —
 * see the comment at the top of `overlay.css` for why a plain, unscoped
 * `body` selector cannot be trusted to win the cascade, and would leak onto
 * every other Console route besides.
 */
const OVERLAY_BODY_CLASS = 'connaro-overlay-active';

function overlaySocketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/v1/ws`;
}

export type OverlayAlertsPageProps = {
  /**
   * Test seam ONLY. Production always builds its client from the `k` query
   * parameter via `createQueueClient`; tests inject a double here so they can
   * assert on `ackStarted`/`ackCompleted` directly instead of through fetch.
   *
   * Typed with `| undefined` (rather than only the optional `?`) because
   * `exactOptionalPropertyTypes` distinguishes "omitted" from "explicitly
   * undefined", and the test helper below passes the latter.
   */
  client?: QueueClient | undefined;
};

export function OverlayAlertsPage({
  client: injectedClient,
}: OverlayAlertsPageProps = {}): React.JSX.Element {
  /*
   * `outputId` is read from the route for identity only. The API resolves
   * the output entirely from the token (apps/api/src/plugins/output-token.ts)
   * — it never appears in a request this page makes — but keeping it as a
   * dependency below means navigating to a different output's URL rebuilds
   * the client instead of continuing to poll with the previous one's.
   */
  const { outputId } = useParams<{ outputId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('k') ?? undefined;

  const client = useMemo<QueueClient | undefined>(() => {
    if (injectedClient !== undefined) return injectedClient;
    if (token === undefined || token === '') return undefined;
    return createQueueClient({ token });
  }, [injectedClient, token, outputId]);

  const [current, setCurrent] = useState<AlertView | null>(null);
  const [layout, setLayout] = useState<Record<string, unknown>>({});
  const [notice, setNotice] = useState<string | null>(
    client === undefined ? MISSING_TOKEN_MESSAGE : null,
  );

  /*
   * Unconditional, and separate from the poll loop below: the body must
   * never paint, whether an alert is showing, the queue is empty, or the
   * token is missing or rejected. `useLayoutEffect` (not `useEffect`) so the
   * class lands before the browser's first paint, not after it.
   */
  useLayoutEffect(() => {
    document.body.classList.add(OVERLAY_BODY_CLASS);
    return () => {
      document.body.classList.remove(OVERLAY_BODY_CLASS);
    };
  }, []);

  useEffect(() => {
    if (client === undefined) return;

    const state = { disposed: false };
    /*
     * Read through a function, not `state.disposed` directly: TypeScript's
     * control-flow analysis assumes synchronous execution and narrows a
     * boolean to a fixed value across `await`s within one async function,
     * even though the cleanup below flips it from what is, relative to
     * `run()`, a concurrently-running callback. A function call is opaque to
     * that narrowing, so every check below actually re-reads the flag.
     */
    const isDisposed = (): boolean => state.disposed;
    let wakeIdle: (() => void) | null = null;

    /** Waits `ms`, but returns early if `wakeIdle()` is called meanwhile. */
    const idle = (ms: number): Promise<void> =>
      new Promise((resolve) => {
        const timer = setTimeout(resolve, ms);
        wakeIdle = () => {
          clearTimeout(timer);
          resolve();
        };
      });

    const sleep = (ms: number): Promise<void> =>
      new Promise((resolve) => setTimeout(resolve, ms));

    // Narrowed once, outside the closure below: TypeScript does not carry
    // the `client !== undefined` check above into a function declared
    // inside this effect.
    const activeClient = client;

    /*
     * The queue loop. `poll` is the source of truth — it is what claims the
     * next alert server-side — so this keeps calling it on a fixed idle
     * interval even if the WebSocket nudge below never arrives.
     */
    async function run(): Promise<void> {
      while (!isDisposed()) {
        let alerts: AlertView[];
        try {
          const pending = await activeClient.poll();
          alerts = pending.alerts;
          // The output's layout, not any one alert's — held independently of
          // `current` so it survives an empty poll between alerts instead of
          // resetting to the default every time the queue drains.
          if (!isDisposed()) setLayout(pending.layout);
        } catch {
          if (activeClient.stopped) {
            // Terminal: a revoked or rotated token will never start working
            // again, so the loop ends here rather than retrying forever.
            if (!isDisposed()) setNotice(INVALID_TOKEN_MESSAGE);
            return;
          }
          // Transient (500, a dropped connection, 502/503) — keep going.
          if (!isDisposed()) await idle(IDLE_POLL_MS);
          continue;
        }

        // Exactly one alert at a time, whatever else is queued behind it.
        const next = alerts[0];
        if (next === undefined) {
          if (!isDisposed()) setNotice(null);
          if (!isDisposed()) await idle(IDLE_POLL_MS);
          continue;
        }

        if (isDisposed()) return;
        setNotice(null);
        setCurrent(next);

        // Best-effort: a duplicate ack is a 200 on the server, and a failed
        // one here does not stop the alert from being shown for its duration.
        await activeClient.ackStarted(next.id).catch(() => undefined);
        if (isDisposed()) return;

        await sleep(next.durationMs);
        if (isDisposed()) return;

        await activeClient.ackCompleted(next.id).catch(() => undefined);
        if (isDisposed()) return;
        setCurrent(null);
      }
    }

    void run();

    /*
     * The WebSocket nudge (ws/protocol.ts `alerts_pending`). It carries no
     * alert data by design — it only shortens the idle wait above, never
     * replaces `poll` as the transport of record. A socket that never
     * connects, or a browser without one, leaves the loop above correct on
     * its own, just on a fixed interval instead of an immediate wake.
     */
    let socket: WebSocket | undefined;
    try {
      socket = new WebSocket(overlaySocketUrl());
      socket.onopen = () => {
        socket?.send(JSON.stringify({ v: 1, type: 'subscribe_output', token }));
      };
      socket.onmessage = (event: MessageEvent<string>) => {
        let frame: unknown;
        try {
          frame = JSON.parse(event.data);
        } catch {
          return;
        }
        if (typeof frame !== 'object' || frame === null || !('type' in frame)) return;
        const typed = frame as { type: string };
        if (typed.type === 'alerts_pending') wakeIdle?.();
      };
    } catch {
      // No socket, no nudge — the poll loop above is still correct.
    }

    return () => {
      state.disposed = true;
      wakeIdle?.();
      socket?.close();
    };
  }, [client, token]);

  /*
   * `layout` is untrusted JSON from `alert_outputs.layout` (`AlertLayout`,
   * `packages/alerts/src/style.ts`), read the same defensive way
   * `AlertRender` reads `style`. `position` goes through `pickEnum`
   * (`./alerts/style-values.js`), not a bare `typeof` check, because it
   * becomes a class token (`overlay.css`'s `--pos-*` rules) and a
   * `typeof === 'string'` test alone does not stop a string containing a
   * space from smuggling a second, unconfigured class in alongside it
   * (fix round 1).
   */
  const position = pickEnum(layout.position, ALERT_POSITIONS, 'center');
  const maxWidthPercent = typeof layout.maxWidthPercent === 'number' ? layout.maxWidthPercent : 80;

  return (
    <div
      className={`overlay overlay--pos-${position}`}
      style={{ '--alert-max-width': maxWidthPercent } as React.CSSProperties}
    >
      {/*
        * The SAME component the editor previews with (§ Phase D). Extracted
        * from an inline div here so the two cannot diverge — what an operator
        * judged while writing the template is what OBS draws.
        */}
      {current !== null && <AlertRender text={current.text} style={current.style} />}
      {notice !== null && <div className="overlay__notice">{notice}</div>}
    </div>
  );
}
